import React, { useEffect, useMemo } from 'react';
import type { CardPreviewPaneProps, GalleryAsset } from '../GenericPreviewPane';
import { AssetContainer, GenericPreviewPane } from '../GenericPreviewPane';
import { StationeryDraftFormat, StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { VimeoVideo } from '@shared/components/VimeoVideo';
import { useTranslation } from '@shared/core';
import { usePdpColorways } from '../../PdpColorwayContext';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { PageScaler } from '@apps/card/routes/CardCustomizer/components/PageScaler';
import { FitScaler } from '@apps/card/routes/CardCustomizer/components/FitScaler';
import { CardPreviewFavoriteButton } from './CardPreviewFavoriteButton';
import { useFeatureValue } from '@shared/core/featureFlags';
import { Box } from '@withjoy/joykit';
import { useCardPreviewPaneAssets } from '../useCardPreviewPaneAssets';
import { useSuiteImageAsset } from '../useSuiteImageAsset';
import { InteractiveCardPreview } from './InteractiveCardPreview';
import { useSsrPreviewRenderDelay } from './useSsrPreviewRenderDelay';
import { getDimensions } from '@apps/card/utils';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useInsituProductImage } from '@apps/card/hooks/useInsituProductImage';
import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import { Shadow } from '@apps/card/components/Shadow';

export const getCardBackPreviewImage = (category: StationeryTemplateCategoryEnum) => {
  switch (category) {
    case StationeryTemplateCategoryEnum.thankYou: {
      return 'https://withjoy.com/assets/public/back-variety-preview-thankyoucard-2.jpg';
    }
    case StationeryTemplateCategoryEnum.holiday: {
      return 'https://withjoy.com/media/print/back-variety-preview-holiday.jpg';
    }
    default: {
      return 'https://withjoy.com/assets/public/back-variety-preview-5.jpg';
    }
  }
};

export const getEnvelopePreviewImage = (category: StationeryTemplateCategoryEnum) => {
  switch (category) {
    case StationeryTemplateCategoryEnum.thankYou: {
      return 'https://withjoy.com/assets/public/envelope-variety-preview-thankyoucard-2.jpg';
    }
    case StationeryTemplateCategoryEnum.holiday: {
      return 'https://withjoy.com/media/print/envelope-variety-preview-holiday.jpg';
    }
    default: {
      return 'https://withjoy.com/assets/public/envelope-variety-preview-4.jpg';
    }
  }
};

export const getEnvelopeAddressPreviewImage = (category: StationeryTemplateCategoryEnum) => {
  switch (category) {
    case StationeryTemplateCategoryEnum.thankYou: {
      return 'https://withjoy.com/assets/public/envelope-address-preview-with-recipient-thankyoucard-2.jpg';
    }
    case StationeryTemplateCategoryEnum.holiday: {
      return 'https://withjoy.com/media/print/envelope-address-preview-with-recipient.jpg';
    }
    default: {
      return 'https://withjoy.com/assets/public/envelope-address-preview-with-recipient-3.jpg';
    }
  }
};

type TemplateVideo = Readonly<{ videoId: string; privacyHash: string; thumbnailUrl: string }>;

// Unlisted videos: Vimeo refuses to play them without the privacy hash.
const videosMap: ReadonlyMap<string, TemplateVideo> = new Map([
  [
    'cherie',
    {
      videoId: '1006678838',
      privacyHash: '39a244331a',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923427487-53146c185853a66df439ecd8515b61671203189325fc9a03efdea5fe0d65e44f-d?mw=800&mh=800',
    },
  ],
  [
    'wreath',
    {
      videoId: '1006685494',
      privacyHash: '02dfa8d892',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923435297-f61037cef4528bdac6929ac31a6b2ee9aac4fd91ef8e5b2a9155689c5837285a-d?mw=800&mh=800',
    },
  ],
  [
    'boho_arch',
    {
      videoId: '1006699701',
      privacyHash: '410a8e3fcb',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923451689-0358f01c816a2a257fa02dd0f4365d2694aef3e451da4f57e91af4de7f04e573-d?mw=800&mh=800',
    },
  ],
  [
    'emrald_city',
    {
      videoId: '1006687087',
      privacyHash: '9715acfb12',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923437257-4407abe7258f37795404d4c66e9e0f9367e856458e3df727ffc7ca083b48abbb-d?mw=800&mh=800',
    },
  ],
  [
    'rustic_chic',
    {
      videoId: '1006685928',
      privacyHash: 'e8fde95abd',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923435834-77e65f393fe52c4e8eca62865d981e92fc8d1bb949611d62590d4459b38172ba-d?mw=800&mh=800',
    },
  ],
  [
    'alpine_dream',
    {
      videoId: '1006689172',
      privacyHash: '3c1f277d64',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923439698-01dfd0931eb100fe2545b7a53d27b5ece70320959c6f84101eaf067b46afd0c1-d?mw=800&mh=800',
    },
  ],
  [
    'lovely_doves',
    {
      videoId: '1006698901',
      privacyHash: '3206d3c365',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923450768-e4caa572b35454bffa9af86fcee9a923016ab8baa379af4cda3e169a66c64073-d?mw=800&mh=800',
    },
  ],
  [
    'chateau_light',
    {
      videoId: '1006690614',
      privacyHash: 'bfc2bfeb36',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923441276-ed94d214a6a9506016904e25c03a8cf6075433790356aa35ba9d61a5a68e7143-d?mw=800&mh=800',
    },
  ],
  [
    'gold_confetti',
    {
      videoId: '1006682115',
      privacyHash: '715bbe60e2',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923431538-d136b2184e665b6968b620e03fa1e842731b1f17486a0c0d080eb55c3796e00b-d?mw=800&mh=800',
    },
  ],
  [
    'lavender_leaves',
    {
      videoId: '1006677353',
      privacyHash: '8ce5537b5f',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923429974-2362938e5c1281a059da1de540f2086f4ba12b640f1ac2d008576af6d291a942-d?mw=800&mh=800',
    },
  ],
  [
    'love_and_leaves',
    {
      videoId: '1006682892',
      privacyHash: 'c80621ded0',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923432247-8ff2bf04e8f65de7014d4cb0888cef5117ab0fd3853d9593e3ec63cdaa608b6d-d?mw=800&mh=800',
    },
  ],
  [
    'minimalist_arch',
    {
      videoId: '1006686496',
      privacyHash: '44f401ac5c',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923436513-82840e717d529ecef229b95a30a4c159bcc2ddd67de28394853317ad36be07d4-d?mw=800&mh=800',
    },
  ],
  [
    'bohemian_eucalyptus',
    {
      videoId: '1006684725',
      privacyHash: 'c66c0e3ae0',
      thumbnailUrl: 'https://i.vimeocdn.com/video/1923434421-69f4350731fa46a8e90f19ff92ba315c800a91ec7e4090307b95d215804b1984-d?mw=800&mh=800',
    },
  ],
]);

export const CardPreviewPane = (props: CardPreviewPaneProps) => {
  const { tq } = useTranslation('stationery');
  const isMobile = useIsMobileScreen();
  const { templateCategory, thumbnailUrl, cardFront, cardShape, eventId, templateId, themeId, isFavorite, cardJson } = props;
  const { cssVarStyle, preloadImages, selectedColorwayId } = usePdpColorways();

  const videoDataForTemplate = videosMap.get(themeId);
  const { width, height } = getDimensions(cardJson.card);
  const { activeProduct } = useStationeryProductPricingContext();
  const extraAssets = useCardPreviewPaneAssets('wedding', templateCategory, 'taylor', {
    widthInches: width,
    heightInches: height,
    productType: activeProduct.productType,
  });
  const suiteAsset = useSuiteImageAsset(themeId, selectedColorwayId);
  const { value: interactivePreviewValue } = useFeatureValue('printPdpEnableInteractivePrintPreview');
  const isInteractivePreviewEnabled = interactivePreviewValue === 'on' && !isMobile;

  const { canRenderPreview } = useSsrPreviewRenderDelay();

  useEffect(() => {
    preloadImages?.();
  }, [preloadImages]);

  const insituImageUrl = useInsituProductImage(templateCategory, width, height, thumbnailUrl);

  const assets: Array<GalleryAsset> = useMemo(() => {
    return [
      {
        render: () => {
          const content = (
            <div style={cssVarStyle}>
              <Shadow
                shape={cardShape}
                shadow={BOX_SHADOW_8_14_26}
              >
                <Page
                  width={5}
                  height={7}
                  page={canRenderPreview ? cardFront : { ...cardFront, layers: [] }}
                  shape={cardShape}
                  format={StationeryDraftFormat.print}
                  forcePlaceholderQRCode
                />
              </Shadow>
            </div>
          );

          return (
            <AssetContainer
              key="cardFront"
              label={tq`Front`}
              enforceAspectRatio={!isMobile}
              badge={
                <CardPreviewFavoriteButton
                  themeId={themeId}
                  templateCategory={templateCategory}
                  eventId={eventId || ''}
                  templateId={templateId}
                  isFavorite={isFavorite}
                />
              }
            >
              {isInteractivePreviewEnabled ? (
                <InteractiveCardPreview
                  cardJson={cardJson}
                  cardFront={cardFront}
                />
              ) : isMobile ? (
                <PageScaler>{content}</PageScaler>
              ) : (
                <FitScaler>{content}</FitScaler>
              )}
            </AssetContainer>
          );
        },
        thumbnailUrl: thumbnailUrl,
      },
      // Directly after the front, ahead of the in-situ and video entries, so
      // the suite is always the first thing shoppers see beyond the card face.
      ...(suiteAsset ? [suiteAsset] : []),
      ...(insituImageUrl
        ? [
            {
              render: () => (
                <AssetContainer
                  enforceAspectRatio
                  key="insitu"
                  backgroundImageUrl={insituImageUrl}
                />
              ),
              thumbnailUrl: insituImageUrl,
            },
          ]
        : []),
      ...(videoDataForTemplate
        ? [
            {
              render: () => (
                <AssetContainer
                  key="video"
                  enforceAspectRatio
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                  >
                    <VimeoVideo
                      videoId={videoDataForTemplate.videoId}
                      privacyHash={videoDataForTemplate.privacyHash}
                      title={tq`Card design video`}
                      posterUrl={videoDataForTemplate.thumbnailUrl}
                    />
                  </Box>
                </AssetContainer>
              ),
              thumbnailUrl: videoDataForTemplate.thumbnailUrl,
            },
          ]
        : []),
      ...extraAssets,
    ];
  }, [
    tq,
    thumbnailUrl,
    videoDataForTemplate,
    extraAssets,
    suiteAsset,
    cardShape,
    cardFront,
    isMobile,
    themeId,
    templateCategory,
    eventId,
    templateId,
    isFavorite,
    isInteractivePreviewEnabled,
    cardJson,
    canRenderPreview,
    insituImageUrl,
    cssVarStyle,
  ]);

  return (
    <GenericPreviewPane
      {...props}
      assets={assets}
    />
  );
};
